import { useEffect, useRef, useMemo } from "react";
import useFetchAllConnectionKey from "../hooks/useFetchAllConnectionKey";

/**
 * Custom hook for optimized connection key fetching
 *
 * This hook extracts connection key IDs from research query data sources,
 * fetches only the required connection keys (not all), and updates the context.
 *
 * @param dataSources - Array of data objects that contain research_queries
 * @param setIsLoading - Function to set loading state
 * @param setFetchedConnectionKeys - Function to update context with fetched connection keys
 * @returns Array containing the fetched connection keys
 *
 * @example
 * const connectionKeys = useOptimizedConnectionKeys({
 *   dataSources: [researchQueryForm, researchQueryDetailData],
 *   setIsLoading: setLoading("connectionKey"),
 *   setFetchedConnectionKeys,
 * });
 */
const useOptimizedConnectionKeys = ({
  dataSources = [],
  setIsLoading,
  setFetchedConnectionKeys,
}: {
  dataSources?: any[];
  setIsLoading: (loading: boolean) => void;
  setFetchedConnectionKeys: (keys: any[]) => void;
}) => {
  // Track previous connection key IDs to prevent infinite loops
  const previousConnectionKeyIdsRef = useRef<number[]>([]);

  // Extract required connection key IDs from all data sources
  const requiredConnectionKeyIds = useMemo(() => {
    const allConnectionKeyIds = new Set<number>();

    // Process each data source
    dataSources.forEach((dataSource) => {
      if (dataSource?.research_queries?.length > 0) {
        dataSource.research_queries.forEach((query: any) => {
          if (query.source) {
            // Handle both array (Mixed type) and single object (other types)
            const sources = Array.isArray(query.source)
              ? query.source
              : [query.source];

            sources.forEach((src: any) => {
              if (src?.connection_key_id) {
                allConnectionKeyIds.add(src.connection_key_id);
              }
            });
          }
        });
      }
    });

    // Convert to array and filter out null/undefined values
    const finalConnectionKeyIds = Array.from(allConnectionKeyIds).filter(
      (id) => id != null && id > 0
    );

    // CRITICAL FIX: Only return new array if IDs have actually changed
    const sortedIds = Array.from(new Set(finalConnectionKeyIds)).sort(
      (a, b) => a - b
    );
    const previousIds = previousConnectionKeyIdsRef.current;

    // Compare arrays to prevent infinite loops
    const idsChanged =
      sortedIds.length !== previousIds.length ||
      sortedIds.some((id, index) => id !== previousIds[index]);

    if (idsChanged) {
      previousConnectionKeyIdsRef.current = sortedIds;
      return sortedIds;
    } else {
      return previousConnectionKeyIdsRef.current;
    }
  }, [dataSources]);

  // Fetch only the specific connection keys needed (optimized API call)
  const [connectionKeys] = useFetchAllConnectionKey({
    connectionKeyIds: requiredConnectionKeyIds,
    setIsLoading,
  });

  // Update context with fetched connection keys
  useEffect(() => {
    if (connectionKeys) {
      setFetchedConnectionKeys(connectionKeys);
    }
  }, [connectionKeys, setFetchedConnectionKeys]);

  return connectionKeys;
};

export default useOptimizedConnectionKeys;
